import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import { FaHistory } from 'react-icons/fa'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { parseParametrizatioProductSimilarData } from '@helper/grid'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParametrizationSimilarProductContext,
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate,
  IDefaultDate
} from '@base/@types/providers/Parametrization/SimilarProductProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationSimilarProductHeader from '@base/variables/headers/parametrization/similar-product'

const ParametrizationSimilarProductProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Produto Similar - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)
  const [pageCurrent, setPageCurrent] = useState(1)
  const [total, setTotal] = useState(50)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const [dataHistoricUpdate, updateDataHistoricUpdate] = useImmer<any[]>([])
  const [loadingModalHistoricUpdate, setLoadingModalHistoricUpdate] =
    useState(false)
  const [showingModalHistoricUpdate, setShowingModalHistoricUpdate] =
    useState(false)

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    empresa: 0,
    produtoNovo: 0,
    produtoSimilar: 0,
    politicaDeEstoqueArbitraria: 0,
    cdDeOrigem: [],
    demanda: 0,
    dataInicio: new Date().toDateString(),
    dataFim: new Date().toDateString()
  })

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const initialDate = dayjs(dateNow).subtract(1, 'year').format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).add(1, 'year').format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )

  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getCenters()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false,
    periodo: new Date().toISOString(),
    periodoFim: new Date().toISOString(),
    initialDate: defaultDate.initialDate,
    finalDate: defaultDate.finalDate,
    produtoIdNew: [],
    produtoIdSimilar: [],
    periodoIds: false,
    empresaIds: []
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationSimilarProductHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationSimilarProductHeader')) {
      encrypteHeaderStorange('parametrizationSimilarProductHeader', headers)
    } else {
      localStorage.removeItem('parametrizationSimilarProductHeader')
      encrypteHeaderStorange('parametrizationSimilarProductHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {
    const configuracaoGrid = {
      id: headers[0].columns[0] ? headers[0].columns[0].show : true,
      empresaId: headers[1].columns[0] ? headers[1].columns[0].show : true,
      empresaNome: headers[1].columns[1] ? headers[1].columns[1].show : true,
      produtoNovoId: headers[2].columns[0] ? headers[2].columns[0].show : true,
      produtoNovoNome: headers[2].columns[1]
        ? headers[2].columns[1].show
        : true,
      produtoSimilarId: headers[3].columns[0]
        ? headers[3].columns[0].show
        : true,
      produtoSimilarNome: headers[3].columns[1]
        ? headers[3].columns[1].show
        : true,
      percentualDemandaAbsorvida: headers[4].columns[0]
        ? headers[4].columns[0].show
        : true,
      dataFim: true,
      dataInicio: true
    }
    const filtros = {
      empresaIds: paramsHelper.parse(params.empresaIds),
      // lojaIds: paramsHelper.parse(params.lojasIds),
      produtoNovoIds: paramsHelper.parse(params.produtoIdNew),
      produtoSimilarIds: paramsHelper.parse(params.produtoIdSimilar),
      dataInicio: params.periodoIds === true ? currentyDate.initialDate : null,
      dataFim: params.periodoIds === true ? currentyDate.finalDate : null,
      search: ''
    }

    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      configuracaoGrid: configuracaoGrid,
      filtros: filtros
    }
    try {
      const { data: res } = await parametrization.postExportSimilarProduct(
        paramsToSend
      )
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Erro Requisição.',
        description: message,
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [
    headers,
    params.empresaIds,
    params.produtoIdNew,
    params.produtoIdSimilar,
    params.periodoIds,
    currentyDate.initialDate,
    currentyDate.finalDate,
    filename,
    toast
  ])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationSimilarProductHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationSimilarProductHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'initialDate') {
        setCurrentyDate({
          initialDate: value,
          finalDate: currentyDate.finalDate
        })
      }
      if (name === 'finalDate') {
        setCurrentyDate({
          initialDate: currentyDate.initialDate,
          finalDate: value
        })
      }
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {}, [])

  const getData = useCallback(
    async (search?: any, page?: any, total?: any, isQuery?: boolean) => {
      setLoading(!(page > 1))
      // await getMaintence()
      _setFetching(true)

      try {
        encrypteHeaderStorange('parametrizationSimilarProductHeader', headers)

        handleStorange()

        const paramsToSend = {
          empresaIds: paramsHelper.parse(params.empresaIds),
          // lojaIds: paramsHelper.parse(params.lojasIds),
          produtoNovoIds: paramsHelper.parse(params.produtoIdNew),
          produtoSimilarIds: paramsHelper.parse(params.produtoIdSimilar),
          dataInicio:
            params.periodoIds === true ? currentyDate.initialDate : null,
          dataFim: params.periodoIds === true ? currentyDate.finalDate : null,
          search: search === null ? '' : search
        }
        parametrization
          .getSimilarProductsData(search, page, 50, paramsToSend)
          .then(response => {
            const newData = [...data, ...response.data?.data]

            if (isQuery || page === 1) {
              updateData(response.data?.data)
            } else {
              updateData(newData)
            }
            _setFetching(false)
            setLoading(false)

            const pagination = response.data.pagination
            const xTotal = pagination.totalRows
            updatePagination(pagination)
            setTotal(xTotal)
            setPageCurrent(page)
            // setSelectedOne(0)
            // setSelecteds([])
          })
          .catch(() => {
            updatePagination({
              actualPage: 0,
              rows: 0,
              totalPages: 0,
              totalRows: 0,
              skip: 0
            })
            updateData([])
            _setFetching(false)
            setLoading(false)
          })
      } catch {
        setLoading(false)
      }
    },
    [
      headers,
      handleStorange,
      params,
      currentyDate.initialDate,
      currentyDate.finalDate,
      data,
      updatePagination,
      updateData
    ]
  )

  const updateProductSimilar = useCallback(async () => {
    try {
      const { data: _res } = await parametrization.putProductSimilar({
        params: {
          id: Number(selected),
          empresaId: paramsEdit.empresa.value,
          produtoNovoId: paramsEdit.produtoNovo.value,
          produtoSimilarId: paramsEdit.produtoSimilar.value,
          demandaAbsorvida: Number(paramsEdit.demanda),
          dataInicio: paramsEdit.dataInicio,
          dataFim: paramsEdit.dataFim
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Faturamento Minimo editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [paramsEdit, selected, toast, getData, setLoading, setSelectedOne])

  const deleteData = useCallback(async () => {
    try {
      await parametrization.deleteProductSimilar({
        idMinimumBilling: Number(selected)
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [selected, getData, toast, setLoading, setSelectedOne])

  const createData = useCallback(async () => {
    try {
      await parametrization.postNewProductSimilar({
        params: {
          empresaId: paramsEdit.empresa.value,
          produtoNovoId: paramsEdit.produtoNovo.value,
          produtoSimilarId: paramsEdit.produtoSimilar.value,
          demandaAbsorvida: Number(paramsEdit.demanda),
          dataInicio: paramsEdit.dataInicio,
          dataFim: paramsEdit.dataFim
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Produto similar cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [paramsEdit, getData, toast, setSelectedOne, setLoading])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      // {
      //   id: 'lojasIds',
      //   label: 'Centros',
      //   filterId: 'centers',
      //   type: 'select',
      //   isMulti: true
      // },
      {
        id: 'empresaIds',
        label: 'Empresas',
        filterId: 'companies',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtoIdSimilar',
        label: 'Produtos Similares',
        type: 'select-api-post',
        apiUrl: '/v1/Produto',
        isMulti: true
      },
      {
        id: 'produtoIdNew',
        label: 'Produtos Novos',
        type: 'select-api-post',
        apiUrl: '/v1/Produto',
        isMulti: true
      },
      {
        id: 'periodoIds',
        label: 'Data',
        type: 'checkbox'
      },
      {
        id: 'periodo',
        label: '',
        type: 'date-input',
        isMulti: false
      }
    ]
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterSimilarProduct',
            data,
            headers,
            parser: parseParametrizatioProductSimilarData
          }
        ]
      },
      {
        type: 'parametrizationProcuctSimilarUpload'
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'empresa',
          value: {
            label: idData[0].empresa
              ? idData[0].idEmpresa + ' - ' + idData[0].empresa.descricao
              : '',
            value: idData[0].idEmpresa
          }
        })
        handleUpdateEditParams({
          name: 'produtoNovo',
          value: {
            label: idData[0].produto
              ? idData[0].produto.id + ' - ' + idData[0].produto.descricao
              : '',
            value: idData[0].produto.id
          }
        })
        handleUpdateEditParams({
          name: 'produtoSimilar',
          value: {
            label: idData[0].similar
              ? idData[0].similar.id + ' - ' + idData[0].similar.descricao
              : '',
            value: idData[0].similar.id
          }
        })
        handleUpdateEditParams({
          name: 'demanda',
          value: Number(idData[0].porcentagemDemanda)
        })
        handleUpdateEditParams({
          name: 'dataInicio',
          value: idData[0].dataInicio
            ? new Date(idData[0].dataInicio).toISOString().split('T')[0]
            : new Date().toDateString()
        })
        handleUpdateEditParams({
          name: 'dataFim',
          value: idData[0].dataFim
            ? new Date(idData[0].dataFim).toISOString().split('T')[0]
            : new Date().toDateString()
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selected

      setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
      if (newSelected === similarProduct) {
        updateEditParams({
          empresa: 0,
          produtoNovo: 0,
          produtoSimilar: 0,
          politicaDeEstoqueArbitraria: 0,
          cdDeOrigem: [],
          demanda: 0,
          dataInicio: new Date().toDateString(),
          dataFim: new Date().toDateString()
        })
      } else {
        handleEditParams(index['id-id'])
      }
    },
    [setSelectedOne, handleEditParams, selected, updateEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      },
      {
        type: 'icon',
        icon: <FaHistory />
      }
    ]
  }, [selected, handleSelect])

  const getHistoricUpdate = useCallback(
    async (idSimilar: number) => {
      setLoadingModalHistoricUpdate(true)
      setShowingModalHistoricUpdate(true)
      parametrization
        .getHistoricUpdateProductSimilar(idSimilar)
        .then(({ data: { data } }) => {
          updateDataHistoricUpdate(data)
          setLoadingModalHistoricUpdate(false)
        })
        .catch(() => {
          updateDataHistoricUpdate([])
          setLoadingModalHistoricUpdate(false)
        })
    },
    [updateDataHistoricUpdate]
  )

  const clearAllFilters = () => {
    updateParams({
      lojasIds: [],
      mundoIds: [],
      categoriasCompactasIds: [],
      fornecedoresIds: [],
      produtosIds: [],
      statusIds: [],
      curvaAbc: [],
      curvaIgualZero: false,
      produtoSemFornecedor: false,
      periodo: new Date().toISOString(),
      periodoFim: new Date().toISOString(),
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate,
      produtoIdNew: [],
      produtoIdSimilar: [],
      periodoIds: false,
      empresaIds: []
    })
  }

  const postUpload = useCallback(
    async (eventOrFile: File | any) => {
      setLoading(true)
      const file =
        eventOrFile instanceof File ? eventOrFile : eventOrFile.target.files[0]

      try {
        await parametrization.uploadSupplyProductSimilar({ file })
        getData(null, 1, 50, false)
        toast({
          title: 'Sucesso',
          description: 'Importação realizada com sucesso.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
      } catch (response: any) {
        const data = response?.response?.data
        const message =
          data?.message && data.message !== 'O arquivo não possui informações'
            ? data.message
            : response?.response?.status === 400
            ? data?.message ?? 'Requisição inválida.'
            : 'Ocorreu um problema interno. Tente novamente mais tarde.'

        const details: string[] = Array.isArray(data?.data) ? data.data : []

        if (details.length > 0) {
          const htmlLines = details.map((d: string) => `<li>${d}</li>`).join('')
          Swal.fire({
            title: '<strong>Erro na importação</strong>',
            icon: 'error',
            html: `${message}<ul style="text-align:left;margin-top:8px">${htmlLines}</ul>`,
            showCloseButton: true,
            focusConfirm: false
          })
        } else {
          toast({
            title: 'Erro na importação.',
            description: message,
            status: 'error',
            duration: 4000,
            isClosable: true
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [toast, setLoading, getData]
  )

  const postCarga = useCallback(
    async (
      produtos: Array<{
        EmpresaId: number
        ProdutoNovoId: number
        ProdutoSimilarId: number
        DemandaAbsorvida: number
        DataInicio: string
        DataFim: string
      }>
    ) => {
      setLoading(true)
      try {
        const { data: res } = await parametrization.cargaProdutosSimilares(
          produtos
        )
        if (res?.Success === false) {
          const details: string[] = Array.isArray(res?.Data) ? res.Data : []
          if (details.length > 0) {
            const htmlLines = details
              .map((d: string) => `<li>${d}</li>`)
              .join('')
            Swal.fire({
              title: '<strong>Erro na importação</strong>',
              icon: 'error',
              html: `${res.Message}<ul style="text-align:left;margin-top:8px">${htmlLines}</ul>`,
              showCloseButton: true,
              focusConfirm: false
            })
          } else {
            toast({
              title: 'Erro na importação.',
              description: res.Message,
              status: 'error',
              duration: 4000,
              isClosable: true
            })
          }
          return
        }
        getData(null, 1, 50, false)
        toast({
          title: 'Sucesso',
          description:
            res?.Message ?? 'Produtos Similares carregados com sucesso.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
      } catch (response: any) {
        const errData = response?.response?.data
        const message =
          errData?.message ??
          (response?.response?.status === 400
            ? 'Requisição inválida.'
            : 'Ocorreu um problema interno. Tente novamente mais tarde.')
        const details: string[] = Array.isArray(errData?.Data)
          ? errData.Data
          : []
        if (details.length > 0) {
          const htmlLines = details.map((d: string) => `<li>${d}</li>`).join('')
          Swal.fire({
            title: '<strong>Erro na importação</strong>',
            icon: 'error',
            html: `${message}<ul style="text-align:left;margin-top:8px">${htmlLines}</ul>`,
            showCloseButton: true,
            focusConfirm: false
          })
        } else {
          toast({
            title: 'Erro na importação.',
            description: message,
            status: 'error',
            duration: 4000,
            isClosable: true
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [toast, setLoading, getData]
  )

  return (
    <ParametrizationSimilarProductContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioProductSimilarData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        updateProductSimilar,
        deleteData,
        setFilters,
        setFilename,
        pageCurrent,
        defaultDate,
        currentyDate,
        total,
        showingModalHistoricUpdate,
        loadingModalHistoricUpdate,
        getHistoricUpdate,
        dataHistoricUpdate,
        clearAllFilters,
        postUpload,
        postCarga
      }}
    >
      {children}
    </ParametrizationSimilarProductContext.Provider>
  )
}

export default ParametrizationSimilarProductProvider
export const ParametrizationSimilarProductContext =
  createContext<IParametrizationSimilarProductContext>(
    {} as IParametrizationSimilarProductContext
  )
